import { useState, useEffect, useMemo } from 'react';
import { LazyLoadImage as LazyLoading } from 'react-lazy-load-image-component';
import { useSelector } from 'react-redux';
import SVG from 'react-inlinesvg';

import { gu_filterCommonKey } from '../../../../../util/general-util';

export const LazyLoadImage = ({ src, srcWebP, useWebP = false, fallbackSrc, alt, style, className, hoverImagesSrc, onError, notLazyLoad, afterLoad, isSvg, ...restProps }) => {
    const hostname = window.location.hostname.replace('www.', '');
    const [currentSrc, setCurrentSrc] = useState('');
    const [attempt, setAttempt] = useState(0);
    const [webpUrl, setWebpUrl] = useState(null);
    const [hoverSrc, setHoverSrc] = useState(null);
    const [isHover, setIsHover] = useState(false);
    const portal = useSelector((state) => state.portal);
    const { settings: { staticAssetsDomain, enableDynamicWebStaticDomain, staticAssetsEnterpriseDomain } = {} } = portal || {};

    const dynamicWebDataStaticDomains = useMemo(() => {
        const ssiDomains = enableDynamicWebStaticDomain ? window.dynamicWebDataStaticDomains?.trim() : undefined;
        if (window.dynamicWebDataDisableStaticDomain === '1' || !ssiDomains || ssiDomains.includes('<!--# echo var=')) {
            // from index.html
            return null;
        }
        if (ssiDomains) {
            return ssiDomains.split(/\s+/); // Split by space (NGINX format)
        }
        return null;
    }, []);

    const randomDomain = useMemo(() => {
        const isEnterprise = window.dynamicWebDataIsEnterpriseDomain === '1';
        let selectedDomains = dynamicWebDataStaticDomains;
        if (!selectedDomains) {
            selectedDomains = isEnterprise
                ? gu_filterCommonKey(staticAssetsEnterpriseDomain, false, { customFilterKeys: [hostname] })
                : gu_filterCommonKey(staticAssetsDomain, false, { customFilterKeys: [hostname] });
        }

        const ensureHttps = (domain) => {
            if (!domain || domain.trim() === '') return '';
            return /^https?:\/\//.test(domain) ? domain : `https://${domain}`;
        };
        if (Array.isArray(selectedDomains)) {
            return ensureHttps(selectedDomains[Math.floor(Math.random() * selectedDomains.length)] || '');
        } else if (typeof selectedDomains === 'string') {
            return ensureHttps(selectedDomains);
        }
        return '';
    }, [staticAssetsDomain, hostname, dynamicWebDataStaticDomains]);

    useEffect(() => {
        if ((srcWebP || useWebP) && src?.match(/\.(png|jpe?g)$/i)) {
            setWebpUrl(`${randomDomain || ''}${srcWebP || src.replace(/\.(png|jpe?g)$/, '.webp')}`);
            setHoverSrc(hoverImagesSrc && `${randomDomain || ''}${hoverImagesSrc.replace(/\.(png|jpe?g)$/, '.webp')}`);
        } else {
            setWebpUrl(null); // Reset webpUrl if conditions are not met
            hoverImagesSrc && setHoverSrc(null);
        }
    }, [srcWebP, useWebP, src, randomDomain]);

    useEffect(() => {
        if (attempt === 0) {
            // If randomDomain is not empty, use it; otherwise, use the src directly
            setCurrentSrc(randomDomain ? `${randomDomain}${src}` : src);
            setHoverSrc(hoverImagesSrc && randomDomain ? `${randomDomain}${hoverImagesSrc}` : hoverImagesSrc);
        } else if (attempt === 1 && randomDomain) {
            // Only proceed to this attempt if randomDomain was used initially
            setCurrentSrc(src);
            hoverImagesSrc && setHoverSrc(hoverImagesSrc);
        } else if ((attempt === 1 && !randomDomain) || attempt === 2) {
            // Go directly to fallback if randomDomain is empty, or on the second attempt
            if (fallbackSrc) {
                setCurrentSrc(fallbackSrc);
                hoverImagesSrc && setHoverSrc(hoverImagesSrc);
            }
        }
        if (webpUrl) {
            const checkWebPExisting = new Image();
            checkWebPExisting.src = webpUrl;
            checkWebPExisting.onerror = () => setWebpUrl(null);
        }
    }, [attempt, randomDomain, src, webpUrl]);

    useEffect(() => {
        if (!hoverImagesSrc) {
            setHoverSrc(null);
            setIsHover(false);
        }
    }, [hoverImagesSrc]);

    const handleError = (e) => {
        if (!src) return;
        if (typeof onError === 'function') {
            onError(e);
        } else {
            setAttempt((prev) => prev + 1);
        }
    };

    let imageComponent = (
        <LazyLoading
            loading={restProps?.loading || 'lazy'}
            style={style || {}}
            src={isHover ? hoverSrc : currentSrc}
            className={className || null}
            alt={alt || ''}
            onError={handleError}
            afterLoad={afterLoad}
            onMouseEnter={() => hoverSrc && setIsHover(true)}
            onMouseLeave={() => hoverSrc && setIsHover(false)}
            {...restProps}
        />
    );

    if (notLazyLoad || webpUrl) {
        imageComponent = (
            <img
                style={style || {}}
                src={isHover ? hoverSrc : currentSrc}
                className={className || null}
                onMouseEnter={() => hoverSrc && setIsHover(true)}
                onMouseLeave={() => hoverSrc && setIsHover(false)}
                alt={alt || ''}
                onError={handleError}
                onLoad={afterLoad}
                {...restProps}
            />
        );
    }

    if (isSvg) {
        return (
            <SVG style={style || {}} src={currentSrc} className={className || null} {...restProps}>
                {restProps.children}
            </SVG>
        );
    }

    return webpUrl ? (
        <picture onMouseEnter={() => hoverSrc && setIsHover(true)} onMouseLeave={() => hoverSrc && setIsHover(false)}>
            <source type='image/webp' srcSet={isHover ? hoverSrc : webpUrl} />
            {imageComponent}
        </picture>
    ) : (
        imageComponent
    );
};
